/**
 * @fileoverview Recurring, programmable periods a habit is due on.
 * @module lib/domain/schedule
 *
 * @description
 * Dates travel as `YYYY-MM-DD` calendar keys, never as `Date` instances with
 * a time-of-day: a habit is due "today" or it is not, and mixing in a clock
 * time is how "due" silently flips at midnight in the wrong timezone.
 */

import type { HabitSchedule, Weekday } from "@/types/types"

/** Short French weekday labels, `Date#getDay()` order (0 = Sunday). */
export const WEEKDAY_LABELS = [
	"Dim",
	"Lun",
	"Mar",
	"Mer",
	"Jeu",
	"Ven",
	"Sam",
] as const

/**
 * Formats a `Date` as a local calendar key.
 *
 * @param date - The date to format.
 * @returns `YYYY-MM-DD`, in the local timezone.
 */
export function toDateKey(date: Date): string {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${String(year)}-${month}-${day}`
}

/**
 * Parses a calendar key back into a local midnight `Date`.
 *
 * @param key - A `YYYY-MM-DD` calendar key.
 * @returns The corresponding local date, at midnight.
 */
export function parseDateKey(key: string): Date {
	const [year, month, day] = key.split("-").map(Number)
	return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

/**
 * Today's calendar key, in the local timezone.
 *
 * @returns `YYYY-MM-DD` for right now.
 */
export function todayKey(): string {
	return toDateKey(new Date())
}

/**
 * Whole calendar days between two keys.
 *
 * @param from - Earlier calendar key.
 * @param to - Later calendar key.
 * @returns `to - from`, in days. Negative when `to` is before `from`.
 */
export function daysBetween(from: string, to: string): number {
	const ms = parseDateKey(to).getTime() - parseDateKey(from).getTime()
	return Math.round(ms / 86_400_000)
}

/**
 * Whether a habit is due on a given calendar date.
 *
 * @param schedule - The habit's recurring period.
 * @param dateKey - The calendar date to test, `YYYY-MM-DD`.
 * @param createdAt - ISO-8601 creation date; the anchor for an interval.
 * @returns True when the habit is due that day.
 *
 * @example
 * isDueOn({ type: "weekly", days: [1, 3, 5] }, "2026-09-16", habit.createdAt)
 */
export function isDueOn(
	schedule: HabitSchedule,
	dateKey: string,
	createdAt: string
): boolean {
	if (schedule.type === "daily") return true

	if (schedule.type === "weekly") {
		const weekday = parseDateKey(dateKey).getDay() as Weekday
		return schedule.days.includes(weekday)
	}

	const anchorKey = toDateKey(new Date(createdAt))
	const elapsed = daysBetween(anchorKey, dateKey)
	const every = Math.max(1, Math.round(schedule.everyNDays))

	return elapsed >= 0 && elapsed % every === 0
}

/**
 * Human-readable summary of a schedule, in French.
 *
 * @param schedule - The habit's recurring period.
 * @returns A short description, e.g. `"Lun, Mer, Ven"`.
 */
export function describeSchedule(schedule: HabitSchedule): string {
	if (schedule.type === "daily") return "Tous les jours"

	if (schedule.type === "weekly") {
		if (schedule.days.length === 0) return "Aucun jour sélectionné"

		return [...schedule.days]
			.sort((a, b) => a - b)
			.map(day => WEEKDAY_LABELS[day])
			.join(", ")
	}

	return schedule.everyNDays <= 1
		? "Tous les jours"
		: `Tous les ${String(schedule.everyNDays)} jours`
}
